import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Gamepad2, ImageUp, X } from "lucide-react";

const I = "w-full px-3 py-2 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500";

function FF({ label, hint, children }) {
  return (
    <label className="block mb-4">
      <div className="text-xs font-semibold text-zinc-600 mb-1">{label}</div>
      {children}
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </label>
  );
}

function UploadImage({ value, onChange, label, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rel = r.data.url || r.data.path || "";
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const finalUrl = rel.startsWith("http") ? rel : (rel.startsWith("/api/") ? `${backend}${rel}` : rel);
      onChange(finalUrl);
    } catch { toast.error("Nahrání selhalo"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="" className="max-h-32 rounded-xl border border-zinc-200"/>
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 p-1 bg-white border border-zinc-200 rounded-full shadow-sm hover:bg-rose-50" data-testid={`${testid}-clear`}><X className="w-3.5 h-3.5"/></button>
        </div>
      ) : (
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-zinc-300 hover:border-purple-500 hover:bg-purple-50/40 cursor-pointer text-sm">
          <ImageUp className="w-4 h-4"/>
          {busy ? "Nahrávám…" : (label || "Nahrát")}
          <input type="file" accept="image/*" className="hidden" onChange={handle} data-testid={testid}/>
        </label>
      )}
    </div>
  );
}

export default function GamingSettingsAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/site-settings").then((r) => setForm(r.data)); }, []);

  if (!form) return <div>Načítání…</div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/site-settings", {
        gaming_enabled: form.gaming_enabled,
        gaming_nav_label: form.gaming_nav_label,
        gaming_logo_url: form.gaming_logo_url,
        gaming_hero_title: form.gaming_hero_title,
        gaming_hero_subtitle: form.gaming_hero_subtitle,
        gaming_hero_description: form.gaming_hero_description,
        gaming_hero_image: form.gaming_hero_image,
        gaming_shipping_fee: Number(form.gaming_shipping_fee),
        gaming_free_shipping_over: Number(form.gaming_free_shipping_over || 0),
      });
      toast.success("Nastavení uloženo. Změny se projeví po znovunačtení.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba při ukládání");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-gaming-settings">
      <div className="mb-5">
        <div className="font-display font-bold text-2xl inline-flex items-center gap-2"><Gamepad2 className="w-6 h-6 text-purple-600"/> Gaming – nastavení stránky</div>
        <div className="text-sm text-zinc-500 mt-0.5">Ovládá vzhled a viditelnost sekce <code className="px-1 py-0.5 bg-zinc-100 rounded text-xs">/gaming</code>.</div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!form.gaming_enabled} onChange={(e) => set("gaming_enabled", e.target.checked)} className="w-4 h-4 accent-purple-600" data-testid="gs-enabled"/>
          <span className="font-semibold">Zobrazit sekci Gaming v hlavní navigaci</span>
        </label>
        <div className="text-xs text-zinc-500 mt-1">Když je vypnuto, odkaz zmizí z menu a stránka /gaming přestane být propagována. URL /gaming je stále přístupná pro testování.</div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4">
        <div className="font-semibold text-sm text-zinc-500 uppercase tracking-widest mb-3">Navigace</div>
        <FF label="Popisek v menu"><input value={form.gaming_nav_label || ""} onChange={(e) => set("gaming_nav_label", e.target.value)} className={I} placeholder="Gaming" data-testid="gs-nav-label"/></FF>
        <FF label="Logo pro sekci Gaming (v hlavičce, když je uživatel na /gaming)" hint="Ponechte prázdné pro použití hlavního loga.">
          <UploadImage value={form.gaming_logo_url} onChange={(v) => set("gaming_logo_url", v)} label="Nahrát logo" testid="gs-logo-upload"/>
        </FF>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4">
        <div className="font-semibold text-sm text-zinc-500 uppercase tracking-widest mb-3">Hero (horní část stránky)</div>
        <FF label="Nadpis"><input value={form.gaming_hero_title || ""} onChange={(e) => set("gaming_hero_title", e.target.value)} className={I} placeholder="Gaming shop" data-testid="gs-hero-title"/></FF>
        <FF label="Podtitulek"><input value={form.gaming_hero_subtitle || ""} onChange={(e) => set("gaming_hero_subtitle", e.target.value)} className={I} placeholder="Nové i bazarové hry, konzole a příslušenství." data-testid="gs-hero-subtitle"/></FF>
        <FF label="Popis" hint="Zobrazí se pod podtitulkem. Můžete použít více řádků.">
          <textarea rows={4} value={form.gaming_hero_description || ""} onChange={(e) => set("gaming_hero_description", e.target.value)} className={I} data-testid="gs-hero-desc"/>
        </FF>
        <FF label="Obrázek na pozadí hero sekce (velký banner)" hint="Doporučení 1920×800 px. Použije se s tmavým překrytím pro čitelnost textu.">
          <UploadImage value={form.gaming_hero_image} onChange={(v) => set("gaming_hero_image", v)} label="Nahrát banner" testid="gs-hero-img"/>
        </FF>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-4">
        <div className="font-semibold text-sm text-zinc-500 uppercase tracking-widest mb-3">Doručení fyzického zboží</div>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Poplatek za dopravu (Kč)" hint="Přičte se v košíku jen když je tam alespoň 1 fyzický gaming produkt.">
            <input type="number" step="0.01" min="0" value={form.gaming_shipping_fee ?? 99} onChange={(e) => set("gaming_shipping_fee", e.target.value)} className={I} data-testid="gs-ship-fee"/>
          </FF>
          <FF label="Doprava zdarma od (Kč)" hint="Ponechte 0 pro vypnutí. Např. 2000 = doprava zdarma nad 2000 Kč.">
            <input type="number" step="0.01" min="0" value={form.gaming_free_shipping_over ?? 0} onChange={(e) => set("gaming_free_shipping_over", e.target.value)} className={I} data-testid="gs-free-ship"/>
          </FF>
        </div>
      </div>

      <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="gs-save">
        {saving ? "Ukládám…" : "Uložit nastavení"}
      </button>
    </form>
  );
}
